import { useState } from 'react';
import {
  PlanDistributionChart,
  UsageTrendsChart,
  ConversionFunnelChart,
  TopFeaturesChart,
  LimitExceededCard,
  MRRTrendChart,
  DateRangeSelector,
} from '@/components/admin/analytics';
import { DateRange } from '@/types/admin/analytics';

/**
 * Admin Analytics Dashboard
 * Main page for viewing subscription and usage analytics
 * Styled to match the user-facing UsageDashboardPage
 */
export function AnalyticsDashboard() {
  const [dateRange, setDateRange] = useState<DateRange>({
    start: '-30d',
    end: 'now',
  });

  return (
    <div className="min-h-screen bg-brand-light-gray animate-fadeIn">
      {/* Header Section - Brand styled */}
      <div className="max-w-7xl mx-auto py-8 px-4 sm:px-6 lg:px-8">
        {/* Title with accent bars */}
        <div className="mb-10 text-center">
          <div className="flex items-center justify-center gap-3 mb-3">
            <div className="w-1 h-8 bg-gradient-to-b from-brand-electric-blue to-brand-dark-blue rounded-full"></div>
            <h1 className="text-4xl font-bold text-gray-900 tracking-tight">
              Analytics Dashboard
            </h1>
            <div className="w-1 h-8 bg-gradient-to-b from-brand-electric-blue to-brand-dark-blue rounded-full"></div>
          </div>
          <p className="text-lg text-gray-600">
            Subscription analytics, usage trends, and revenue metrics
          </p>
        </div>

        {/* Admin Summary Card - Brand gradient */}
        <div className="relative mb-10 overflow-hidden rounded-2xl shadow-2xl">
          <div className="absolute inset-0 bg-gradient-to-br from-brand-electric-blue via-brand-dark-blue to-[#0a1f44]"></div>
          <div className="absolute inset-0 bg-[url('data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iMjAwIiBoZWlnaHQ9IjIwMCIgeG1sbnM9Imh0dHA6Ly93d3cudzMub3JnLzIwMDAvc3ZnIj48ZGVmcz48cGF0dGVybiBpZD0iZ3JpZCIgd2lkdGg9IjQwIiBoZWlnaHQ9IjQwIiBwYXR0ZXJuVW5pdHM9InVzZXJTcGFjZU9uVXNlIj48cGF0aCBkPSJNIDQwIDAgTCAwIDAgMCA0MCIgZmlsbD0ibm9uZSIgc3Ryb2tlPSJ3aGl0ZSIgc3Ryb2tlLW9wYWNpdHk9IjAuMDUiIHN0cm9rZS13aWR0aD0iMSIvPjwvcGF0dGVybj48L2RlZnM+PHJlY3Qgd2lkdGg9IjEwMCUiIGhlaWdodD0iMTAwJSIgZmlsbD0idXJsKCNncmlkKSIvPjwvc3ZnPg==')] opacity-30"></div>
          
          <div className="relative p-8">
            <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-6">
              <div>
                <div className="inline-flex items-center gap-2 mb-3">
                  <svg className="w-5 h-5 text-white/80" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
                  </svg>
                  <span className="text-white/90 text-sm font-medium uppercase tracking-wider">
                    Admin Panel
                  </span>
                </div>
                <h2 className="text-4xl font-bold text-white mb-2">
                  Business Insights
                </h2>
                <p className="text-white/80 text-sm">
                  Real-time metrics and analytics for your platform
                </p>
              </div>
              
              <div className="flex items-center gap-4">
                <div className="text-right">
                  <p className="text-white/60 text-xs uppercase tracking-wider">Last Updated</p>
                  <p className="text-white font-medium">
                    {new Date().toLocaleTimeString()}
                  </p>
                </div>
                <div className="w-12 h-12 bg-white/10 backdrop-blur-sm rounded-full flex items-center justify-center">
                  <svg className="w-6 h-6 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                  </svg>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Date Range Selector - Styled card */}
        <div className="mb-8 bg-white rounded-2xl shadow-lg p-6 border border-gray-100">
          <DateRangeSelector value={dateRange} onChange={setDateRange} />
        </div>

        {/* Charts Grid - 2 columns on medium+ screens */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-8 auto-rows-fr">
          {/* Row 1: Plan Distribution & Usage Trends */}
          <div className="min-h-[520px]">
            <PlanDistributionChart dateRange={dateRange} />
          </div>
          <div className="min-h-[520px]">
            <UsageTrendsChart dateRange={dateRange} />
          </div>

          {/* Row 2: Conversion Funnel & Top Features */}
          <div className="min-h-[520px]">
            <ConversionFunnelChart dateRange={dateRange} />
          </div>
          <div className="min-h-[520px]">
            <TopFeaturesChart dateRange={dateRange} />
          </div>

          {/* Row 3: Limit Exceeded & MRR Trend */}
          <div className="min-h-[520px]">
            <LimitExceededCard dateRange={dateRange} />
          </div>
          <div className="min-h-[520px]">
            <MRRTrendChart dateRange={dateRange} />
          </div>
        </div>

        {/* Info Footer - Brand styled */}
        <div className="relative overflow-hidden rounded-xl shadow-lg border border-brand-electric-blue/20">
          <div className="absolute inset-0 bg-gradient-to-r from-brand-dark-blue/5 to-brand-electric-blue/10"></div>
          <div className="relative p-6">
            <div className="flex items-start gap-4">
              <div className="flex-shrink-0 w-10 h-10 bg-brand-electric-blue/10 rounded-full flex items-center justify-center">
                <svg className="w-5 h-5 text-brand-electric-blue" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
              </div>
              <div>
                <h3 className="font-semibold text-brand-dark-blue mb-1">Dashboard Information</h3>
                <p className="text-sm text-gray-600">
                  All charts automatically refresh every 5 minutes. Data is cached for optimal performance.
                  Use the date range selector above to filter data across all time-based charts.
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
